// The agents area's pure rules (spec 14 §4.2 to §4.4, D-509, D-511, D-512),
// kept out of the screens so each is unit-tested without a DOM.

import type { components } from "@assessly/api-client";

type S = components["schemas"];
export type AgentListItem = S["AgentListItem"];
export type AgentDetail = S["AgentDetail"];
export type AgentPolicyItem = S["AgentPolicyItem"];
export type AgentToolItem = S["AgentToolItem"];
export type AgentAction = S["AgentAction"];
export type AgentGuardrailEvent = S["AgentGuardrailEvent"];
export type AgentPromptVersion = S["AgentPromptVersion"];
export type AgentMembership = S["AgentMembership"];
export type AgentSettings = S["AgentSettings"];
export type PolicyAutonomy = AgentPolicyItem["autonomy"];
export type DeclaredAutonomy = AgentPolicyItem["max_autonomy"];
export type ActionStatus = AgentAction["status"];
export type GuardrailKind = AgentGuardrailEvent["kind"];

// ---- the autonomy ladder (D-509 ruling 1, spec 14 §2.4) ----------------------

/** Strictest first: a value's index is how far it lets the agent act alone. */
export const AUTONOMY_LADDER: readonly PolicyAutonomy[] = [
  "disabled",
  "ask",
  "threshold",
  "autonomous_with_cap",
  "autonomous",
];

export function autonomyRank(value: PolicyAutonomy): number {
  return AUTONOMY_LADDER.indexOf(value);
}

/**
 * The autonomy values the policy select offers for a write (spec 14 §2.4,
 * §4.3, D-509 ruling 1):
 *
 *   - never laxer than the tool's `max_autonomy` (422 AGENT_AUTONOMY_NOT_ALLOWED);
 *   - never `autonomous` or `autonomous_with_cap` on an irreversible tool
 *     (422 AGENT_IRREVERSIBLE_NOT_AUTONOMOUS);
 *   - laxer than the row's current effective value only for a holder of
 *     `agents.config.manage` (403 AGENT_POLICY_LAXER_REQUIRES_CONFIG).
 *
 * The current value is always offered, so the select can show it even when a
 * later rule would not admit it as a choice.
 */
export function allowedAutonomies(input: {
  readonly current: PolicyAutonomy;
  readonly max: DeclaredAutonomy;
  readonly reversibility: AgentToolItem["reversibility"];
  readonly canConfig: boolean;
  /** A read takes its declared default or Disabled and nothing between
   * (spec 14 §2.4, D-543 ruling 21). */
  readonly kind?: "read" | "write";
  readonly declared?: DeclaredAutonomy;
}): PolicyAutonomy[] {
  const maxRank = autonomyRank(input.max);
  const currentRank = autonomyRank(input.current);
  return AUTONOMY_LADDER.filter((value) => {
    if (value === input.current) return true;
    const rank = autonomyRank(value);
    if (input.kind === "read" && value !== "disabled" && value !== (input.declared ?? input.max)) {
      return false;
    }
    if (rank > maxRank) return false;
    if (
      input.reversibility === "irreversible" &&
      (value === "autonomous" || value === "autonomous_with_cap")
    ) {
      return false;
    }
    if (!input.canConfig && rank > currentRank) return false;
    return true;
  });
}

// ---- policy numbers (spec 14 §2.4, AgentPolicyParams) ------------------------

/** Which policy-number editor a tool's `policy_params_schema` asks for. */
export type PolicyParamsShape = "cap" | "cap_reliability" | "facility_caps" | "none";

export function policyParamsShape(
  schema: AgentToolItem["policy_params_schema"],
): PolicyParamsShape {
  if (schema === null) return "none";
  const properties = schema["properties"];
  if (properties === null || typeof properties !== "object") return "none";
  const keys = Object.keys(properties);
  if (keys.includes("facility_caps")) return "facility_caps";
  if (keys.includes("cap_minor")) {
    return keys.includes("min_reliability") ? "cap_reliability" : "cap";
  }
  return "none";
}

/** Whether a policy row carries numbers an unsupervised action spends against. */
export function hasPolicyNumbers(params: AgentPolicyItem["policy_params"]): boolean {
  if (params === null) return false;
  if ("facility_caps" in params) return params.facility_caps.length > 0;
  return true;
}

/**
 * D-507 ruling 5: a policy row with numbers on an agent whose monthly spend
 * cap is 0 cannot act unsupervised until the cap is raised.
 */
export function spendCapBlocksPolicy(
  params: AgentPolicyItem["policy_params"],
  monthlySpendCapMinor: number,
): boolean {
  return monthlySpendCapMinor === 0 && hasPolicyNumbers(params);
}

// ---- Hub meters (spec 14 §4.2, D-512 rulings 4 and 5) ------------------------

/** A filled share of a cap, clamped to [0, 1]; undefined when there is no cap to fill. */
export function meterShare(used: number, cap: number): number | undefined {
  if (cap <= 0) return undefined;
  return Math.max(0, Math.min(1, used / cap));
}

export interface SpendMeter {
  /** Served only under every contributing tool's value grants; undefined when fenced. */
  readonly spendMinor: number | undefined;
  readonly capMinor: number;
  readonly currency: string;
  readonly share: number | undefined;
  readonly capReached: boolean;
}

/**
 * The spend bar: MTD spend over the cap (D-512 ruling 5). The money is absent
 * when the reader lacks a contributing tool's value grant (ruling 4), and then
 * the bar is not drawn at all; "Cap reached" is a count fact and stays.
 */
export function spendMeter(agent: AgentListItem): SpendMeter {
  const spendMinor = agent.usage_mtd.spend_minor;
  return {
    spendMinor,
    capMinor: agent.caps.monthly_spend_cap_minor,
    currency: agent.usage_mtd.spend_currency ?? agent.caps.spend_cap_currency,
    share:
      spendMinor === undefined
        ? undefined
        : meterShare(spendMinor, agent.caps.monthly_spend_cap_minor),
    capReached: agent.usage_mtd.cap_reached,
  };
}

/** The 30-day sparkline as SVG polyline points in a `width` × `height` box. */
export function sparklinePoints(
  days: readonly { readonly actions: number }[],
  width: number,
  height: number,
): string {
  if (days.length === 0) return "";
  const max = Math.max(1, ...days.map((d) => d.actions));
  const step = days.length === 1 ? 0 : width / (days.length - 1);
  return days
    .map((d, i) => {
      const x = days.length === 1 ? width / 2 : i * step;
      const y = height - (d.actions / max) * height;
      return `${round1(x)},${round1(y)}`;
    })
    .join(" ");
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The enable control renders only when every tool on the agent's list is registered (D-512 ruling 3). */
export function canToggleStatus(agent: AgentListItem, canManage: boolean): boolean {
  return canManage && agent.tools_registered && agent.status !== "disabled";
}

/** The status the toggle moves to. */
export function toggledStatus(status: AgentListItem["status"]): "enabled" | "paused" {
  return status === "enabled" ? "paused" : "enabled";
}

// ---- the journal (spec 14 §4.4, D-512 ruling 7) -------------------------------

export const JOURNAL_TABS = [
  "mine",
  "proposed",
  "executed",
  "rejected",
  "failed",
  "expired",
  "undone",
] as const;
export type JournalTab = (typeof JOURNAL_TABS)[number];

/** The list query a tab asks for. "Needs my decision" is the proposed rows the
 * reader passes §7.2 for, which the server computes (`needs_my_decision`). */
export function tabQuery(tab: JournalTab): {
  status: ActionStatus;
  needs_my_decision?: "true";
} {
  switch (tab) {
    case "mine":
      return { status: "proposed", needs_my_decision: "true" };
    case "proposed":
      return { status: "proposed" };
    default:
      return { status: tab };
  }
}

export const JOURNAL_SORTS = ["created_at", "-created_at", "expires_at", "-expires_at"] as const;
export type JournalSort = (typeof JOURNAL_SORTS)[number];
export type JournalSortKey = "created_at" | "expires_at";

export interface JournalSearch {
  readonly tab?: JournalTab | undefined;
  readonly agent?: string | undefined;
  readonly tool?: string | undefined;
  readonly facility?: string | undefined;
  readonly department?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  /** Impact floor in minor units (value holders only, D-512). */
  readonly impact?: number | undefined;
  readonly sort?: JournalSort | undefined;
  /** One card opened on its own, the target of a notification's deep link. */
  readonly action?: string | undefined;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;
const TOOL_RE = /^[a-z]+\.[a-z0-9_]+$/u;

function uuidOf(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

function dateOf(value: unknown): string | undefined {
  return typeof value === "string" && DATE_RE.test(value) ? value : undefined;
}

/** The journal's URL state; anything malformed reads as absent. */
export function validateJournalSearch(raw: Record<string, unknown>): JournalSearch {
  const tab = (JOURNAL_TABS as readonly string[]).includes(String(raw["tab"]))
    ? (raw["tab"] as JournalTab)
    : undefined;
  const sort = (JOURNAL_SORTS as readonly string[]).includes(String(raw["sort"]))
    ? (raw["sort"] as JournalSort)
    : undefined;
  const impactRaw = raw["impact"];
  const impactNumber =
    typeof impactRaw === "number"
      ? impactRaw
      : typeof impactRaw === "string" && /^\d{1,15}$/u.test(impactRaw)
        ? Number(impactRaw)
        : undefined;
  const impact =
    impactNumber !== undefined && Number.isSafeInteger(impactNumber) && impactNumber >= 0
      ? impactNumber
      : undefined;
  const tool =
    typeof raw["tool"] === "string" && TOOL_RE.test(raw["tool"]) ? raw["tool"] : undefined;
  return {
    tab,
    agent: uuidOf(raw["agent"]),
    tool,
    facility: uuidOf(raw["facility"]),
    department: uuidOf(raw["department"]),
    from: dateOf(raw["from"]),
    to: dateOf(raw["to"]),
    impact,
    sort,
    action: uuidOf(raw["action"]),
  };
}

/** A date filter's day boundary as the instant the list query takes. */
export function dayStartIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

export function dayEndIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

// ---- a card (spec 14 §4.4) ---------------------------------------------------

/** The per-tool card template (D-512, spec 14 §4.4). */
export type CardTemplate =
  "send_quote" | "draft_quote" | "reply_order_status" | "send_text" | "generic";

export function cardTemplateOf(toolCode: string): CardTemplate {
  switch (toolCode) {
    case "sales.send_quote":
      return "send_quote";
    case "sales.draft_quote":
      return "draft_quote";
    case "sales.reply_order_status":
      return "reply_order_status";
    case "messaging.send_text":
      return "send_text";
    default:
      return "generic";
  }
}

/** An action's verdict evidence as a plain record (every variant is closed, D-506 ruling 8). */
export function evidenceOf(action: AgentAction): Readonly<Record<string, unknown>> {
  const evidence = action.evidence;
  return evidence ?? {};
}

/** A string param, when the tool's params carry it. */
export function stringParam(action: AgentAction, key: string): string | undefined {
  const value = action.params[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export type ImpactTone = "bad" | "warn" | "neutral";

/**
 * The money chip's red scale (spec 14 §4.4 "₸ amount red-scaled"): against the
 * cap the verdict measured the action by, at or over the cap reads bad, half
 * of it or more reads warn, and below that neutral. With no cap in the
 * evidence there is nothing to scale against, so the chip reads warn: money
 * an agent would move always deserves a look.
 */
export function impactTone(action: AgentAction): ImpactTone {
  if (action.impact_minor === undefined) return "neutral";
  const capRaw = evidenceOf(action)["cap_minor"];
  const cap = typeof capRaw === "number" && capRaw > 0 ? capRaw : undefined;
  if (cap === undefined) return "warn";
  const share = Math.abs(action.impact_minor) / cap;
  if (share >= 1) return "bad";
  if (share >= 0.5) return "warn";
  return "neutral";
}

/** What the card's undo control does (D-511 ruling 2). */
export type UndoAffordance = "post" | "manual" | "none";

export function undoAffordance(action: AgentAction): UndoAffordance {
  if (action.status !== "executed") return "none";
  if (action.undo_mode === "compensate_manual") return "manual";
  if (
    (action.undo_mode === "reverse" || action.undo_mode === "compensate_tool") &&
    action.can_undo
  ) {
    return "post";
  }
  return "none";
}

/** The most cards one bulk approve may carry (spec 14 §4.4). */
export const BULK_APPROVE_MAX = 10;

/**
 * Whether a card can join the bulk selection: a proposed card the reader may
 * approve, of the same tool as the cards already chosen, while fewer than ten
 * are chosen.
 */
export function bulkSelectable(action: AgentAction, selected: readonly AgentAction[]): boolean {
  if (action.status !== "proposed" || !action.can_approve) return false;
  if (selected.some((s) => s.id === action.id)) return true;
  if (selected.length >= BULK_APPROVE_MAX) return false;
  const first = selected[0];
  return first === undefined || first.tool_code === action.tool_code;
}

export interface Countdown {
  readonly expired: boolean;
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
}

/** Time left before a proposed card expires. */
export function countdown(expiresAt: string, nowMs: number): Countdown {
  const left = Date.parse(expiresAt) - nowMs;
  if (!Number.isFinite(left) || left <= 0) return { expired: true, days: 0, hours: 0, minutes: 0 };
  const minutesTotal = Math.ceil(left / 60_000);
  return {
    expired: false,
    days: Math.floor(minutesTotal / 1440),
    hours: Math.floor((minutesTotal % 1440) / 60),
    minutes: minutesTotal % 60,
  };
}

/** Under an hour left: the countdown is urgent. */
export function countdownUrgent(c: Countdown): boolean {
  return !c.expired && c.days === 0 && c.hours === 0;
}

/**
 * The record an executed action's result names, as a screen this SPA routes
 * (`result.url` when the server gives one, else the reference's own page for
 * the kinds the office has a screen for).
 */
export function resultHref(result: AgentAction["result"]): string | undefined {
  if (result === null) return undefined;
  if (result.url !== undefined) return result.url;
  const [kind, id] = result.ref.split(":");
  if (id === undefined) return undefined;
  switch (kind) {
    case "quote":
      return `/sales/quotes/${id}`;
    case "order":
      return `/sales/orders/${id}`;
    case "work_order":
      return `/production/work-orders/${id}`;
    case "inspection":
      return `/quality/inspections/${id}`;
    default:
      return undefined;
  }
}

/** A guardrail event of these kinds in the last 24 h raises the Hub banner (spec 14 §4.2). */
export const BANNER_GUARDRAIL_KINDS: readonly GuardrailKind[] = [
  "prompt_injection_suspected",
  "cap_spend_exceeded",
];

export const GUARDRAIL_KINDS: readonly GuardrailKind[] = [
  "prompt_injection_suspected",
  "tool_denied_visibility",
  "tool_denied_policy",
  "cap_action_exceeded",
  "cap_spend_exceeded",
  "cap_token_exceeded",
  "schema_validation_failed",
  "citation_missing",
  "scope_violation_blocked",
  "tool_orphaned",
  "run_limit_exceeded",
];

/** The typed confirmation a prompt override needs (spec 14 §4.3). */
export const OVERRIDE_CONFIRMATION = "OVERRIDE";

// ---- money entry -------------------------------------------------------------

/**
 * A typed major-unit amount to integer minor units, or undefined when the
 * text is not a plain non-negative amount. `text` is already normalised to
 * the canonical `1234.56` form (sales' `normalizeTypedDecimal`).
 */
export function majorToMinor(text: string, digits: number): number | undefined {
  const match = /^(\d{1,15})(?:\.(\d+))?$/u.exec(text.trim());
  if (match === null) return undefined;
  const whole = match[1] ?? "0";
  const frac = match[2] ?? "";
  if (frac.length > digits) return undefined;
  const minor = Number(whole + frac.padEnd(digits, "0"));
  return Number.isSafeInteger(minor) ? minor : undefined;
}

/** Integer minor units as the plain major text an input holds. */
export function minorToMajorText(minor: number, digits: number): string {
  const text = Math.trunc(Math.abs(minor)).toString();
  if (digits <= 0) return text;
  const padded = text.padStart(digits + 1, "0");
  return `${padded.slice(0, -digits)}.${padded.slice(-digits)}`;
}

/** Whole non-negative integer text, for counts and budgets. */
export function parseCount(text: string, max: number): number | undefined {
  if (!/^\d{1,10}$/u.test(text.trim())) return undefined;
  const value = Number(text.trim());
  return value <= max ? value : undefined;
}
